import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Tbody,
  Tr,
  Td,
  Flex,
  Text,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { ChevronLeft } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getRawMaterialDetailById } from "../../../../redux/api/admin/rawMaterialApi";

function RawMaterialDetail() {
  const location = useLocation();
  const rawMaterialId = location.state?.id || null;
  console.log("Raw Material ID:", rawMaterialId);
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [itemImages, setItemImages] = useState([]);
  const [rawMaterialData, setRawMaterialData] = useState({
    productName: "",
    materialType: "",
    width: "",
    length: "",
    unit: "",
    sku: "",
    purchasePrice: "",
     opening: "",
    inWord: "",
    outWord: "",
    intransit: "",
    available: "",
    hsnCode: "",
    tax: "",
    colour: "",
    vendorDetails: [],
    itemImages: [],
  });

  useEffect(() => {
    if (rawMaterialId) {
      setLoading(true);
      getRawMaterialDetailById(rawMaterialId)
        .then((response) => {
          console.log("Raw Material Detail Response:", response);
          if (response.data && response.data.data) {
            const data = response.data.data;

            let parsedImages = [];
            if (data.img) {
              try {
                if (typeof data.img === "string") {
                  const parsed = JSON.parse(data.img);
                  if (Array.isArray(parsed)) {
                    parsedImages = parsed.filter((url) => typeof url === "string" && url);
                  }
                } else if (Array.isArray(data.img)) {
                  parsedImages = data.img;
                }
              } catch (error) {
                console.error("Error parsing img:", error);
              }
            }

            const vendorDetails = (data.items || []).map((item) => ({
              vendor: [item.vender?.firstName, item.vender?.lastName].filter(Boolean).join(" ") || "N/A",
              vendorShadeNumber: item.venderShadeNo || "N/A",
              vendorPurchasePrice: item.purchasePrize || "N/A",
            }));

            setRawMaterialData({
              productName: data.productName || "",
              width: data.width || "",
              length: data.length || "",
              unit: data.unit || "",
              materialType: data.materialType?.materialType || "",
              sku: data.sku || "",
              purchasePrice: data.purchasePrice || "",
               intransit: data.intransit || "",
              outWord: data.outWord || "",
              inWord: data.inWord || "",
              opening: data.opening || "",
              available: data.available || "",
              hsnCode: data.hsnCode || "",
              tax: data.tax || "",
              colour: data.colour?.colour || "",
              vendorDetails,
              itemImages: parsedImages,
            });
            setItemImages(parsedImages);
          }
        })
        .catch((error) => {
          console.error("Error fetching raw material detail:", error);
          toast({
            title: "Error",
            description: "Failed to fetch raw material details",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [rawMaterialId]);

  const getImageUrl = (image) => {
    if (!image) return "";
    if (typeof image === "object" && image.url) return image.url;
    if (typeof image === "string") return image;
    return "";
  };

  const displayImages = [...(itemImages.length > 0 ? itemImages : rawMaterialData.itemImages)];
  while (displayImages.length < 5) {
    displayImages.push({ url: "" });
  }

  const getMaterialTypeLabel = (type) => {
    return type || "N/A";
  };


  return (
    <Box className="h-[93vh] flex flex-col mb-16">
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="#011638" />
        </div>
      )}

      {/* Header */}
      <Box className="d-flex align-items start justify-content-between sticky top-0 z-10 bg-white shadow-md ps-2 py-2">
        <span className='d-flex gap-2 justify-start items-center'>
          <ChevronLeft
            onClick={() => window.history.back()}
            className="hover:bg-gray-200 rounded-full cursor-pointer"
          />
          <h4 className="mb-0">Raw Material Detail</h4>
        </span>
      </Box>

      {/* Content */}
      <Box className="flex-1 overflow-y-auto p-6">
        <Flex direction={{ base: "column", md: "row" }}
          gap={6}
          mt={7}
          flexWrap="wrap">

          {/* Raw Material Information */}
          <Box flex="1"
            p={{ base: 3, md: 4 }}
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="sm"
            bg="white"
            width={{ base: "100%", md: "50%" }}
            order={{ base: 2, md: 1 }}
          >
            <Heading size="md" mb={4}>
              Raw Material Information
            </Heading>

            <Flex
              gap={6}
              direction={{ base: "column", sm: "row" }}
              align={{ base: "center", sm: "flex-start" }}
            >
              <Box flex="1" width="100%">
                <Table size="sm">
                  <Tbody>
                    <Tr>
                      <Td fontWeight="bold" width="40%">Material Name</Td>
                      <Td>{rawMaterialData.productName || "N/A"}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Material Type</Td>
                      <Td>{getMaterialTypeLabel(rawMaterialData.materialType)}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Width</Td>
                      <Td>{rawMaterialData.width}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Length</Td>
                      <Td>{rawMaterialData.length}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Unit</Td>
                      <Td>{getMaterialTypeLabel(rawMaterialData.unit)}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">SKU</Td>
                      <Td>{rawMaterialData.sku || "N/A"}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Purchase Price</Td>
                      <Td>{rawMaterialData.purchasePrice || "N/A"}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">HSN Code</Td>
                      <Td>{rawMaterialData.hsnCode || "N/A"}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Tax</Td>
                      <Td>{rawMaterialData.tax ? `${rawMaterialData.tax}%` : "N/A"}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Colour</Td>
                      <Td>
                        {rawMaterialData.colour && (
                          <span style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "4px",
                            backgroundColor: rawMaterialData.colour.toLowerCase(),
                            border: "1px solid #ddd",
                            marginRight: "8px",
                            verticalAlign: "middle"
                          }}></span>
                        )}
                        {rawMaterialData.colour || "N/A"}
                      </Td>
                    </Tr>
                     <Tr>
                      <Td fontWeight="bold">Opening</Td>
                      <Td>{rawMaterialData.opening || "N/A"}</Td>
                    </Tr>
                     <Tr>
                      <Td fontWeight="bold">Inrd</Td>
                      <Td>{rawMaterialData.inWord || "N/A"}</Td>
                    </Tr>
                     <Tr>
                      <Td fontWeight="bold">Outward</Td>
                      <Td>{rawMaterialData.outWord || "N/A"}</Td>
                    </Tr>
                     <Tr>
                      <Td fontWeight="bold">In Transit</Td>
                      <Td>{rawMaterialData.intransit || "N/A"}</Td>
                    </Tr>
                     <Tr>
                      <Td fontWeight="bold">Available</Td>
                      <Td>{rawMaterialData.available || "N/A"}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </Flex>
          </Box>

          {/* Images Section */}
          <Box
            flex="1"
            p={{ base: 3, md: 4 }}
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="sm"
            bg="white"
            width={{ base: "100%", md: "50%" }}
            order={{ base: 1, md: 2 }}
          >
            <Heading as="h3" size="md" mb={4}>
              Item Images
            </Heading>

            <div className="grid gap-2 justify-items-center grid-cols-2 lg:grid-cols-3">
              {displayImages.map((image, index) => {
                const imageUrl = getImageUrl(image);
                return (
                  <div key={index}>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={`img${index + 1}`}
                        style={{
                          width: "100%",
                          maxWidth: "180px",
                          height: "180px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent) {
                            const placeholder = document.createElement('div');
                            placeholder.style.width = "100%";
                            placeholder.style.maxWidth = "180px";
                            placeholder.style.height = "180px";
                            placeholder.style.borderRadius = "8px";
                            placeholder.style.backgroundColor = "#f5f5f5";
                            placeholder.style.display = "flex";
                            placeholder.style.alignItems = "center";
                            placeholder.style.justifyContent = "center";
                            placeholder.style.color = "#999";
                            placeholder.innerText = "No Image";
                            parent.appendChild(placeholder);
                            e.target.remove();
                          }
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          maxWidth: "180px",
                          height: "180px",
                          borderRadius: "8px",
                          backgroundColor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#999",
                        }}
                      >
                        No Image
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Box>
        </Flex>

        {/* Vendor Details Table */}
        <Box
          mt={4}
          mb={6}
          p={{ base: 4, md: 6 }}
          borderWidth="1px"
          borderRadius="lg"
          bg="white"
          boxShadow="sm"
        >
          <Heading size="md" mb={4}>
            Vendor Details
          </Heading>

          <Flex gap={4} mb={4}>
            <div className="overflow-x-auto w-full">
              <table className="min-w-[600px] border-collapse border border-gray-200 w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 whitespace-nowrap">S.No.</th>
                    <th className="border p-2 whitespace-nowrap">Vendor</th>
                    <th className="border p-2 whitespace-nowrap">Vendor Shade Number</th>
                    <th className="border p-2 whitespace-nowrap">Vendor Purchase Price</th>
                  </tr>
                </thead>
                <tbody>
                  {rawMaterialData.vendorDetails && rawMaterialData.vendorDetails.length > 0 ? (
                    rawMaterialData.vendorDetails.map((vendor, index) => (
                      <tr key={index}>
                        <td className="border p-2 text-center">{index + 1}</td>
                        <td className="border p-2">{vendor.vendor || "N/A"}</td>
                        <td className="border p-2">{vendor.vendorShadeNumber || "N/A"}</td>
                        <td className="border p-2">{vendor.vendorPurchasePrice || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="border p-2 text-center">No vendor details available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Flex>
        </Box>

     
      </Box>
    </Box>
  );
}

export default RawMaterialDetail;